// One landing page per common format pair -- /convert/pes-to-dst and
// friends -- each the same converter as /convert, but set up for that one
// conversion: its own title and H1 for the search query, the source format
// preselected in the file picker, and the target format as the filled
// download button. The generic /convert stays the place for every other
// pair, and each of these pages links back to it.
//
// Plain data with no imports, unlike the page components: that's what lets
// both ConvertPage.tsx (browser bundle) and index.ts (server-rendered meta,
// sitemap, markdown mirrors) import this one copy, rather than
// hand-duplicating it the way PAGE_META/PAGE_STRUCTURED_DATA are for the
// other pages.

export interface ConvertPairFAQ {
  question: string;
  answer: string;
}

export interface ConvertPair {
  /** URL segment under /convert/, e.g. "pes-to-dst". */
  slug: string;
  /** Lowercase extension without the dot, as /api/convert's files keys use. */
  from: string;
  to: string;
  title: string;
  // Kept under ~160 characters -- see the matching note on HomePage.tsx.
  description: string;
  heading: string;
  intro: string;
  details: string[];
  // Deliberately distinct wording from `details` -- see the matching note on
  // CreatePage.tsx's FAQ_DATA.
  faq: ConvertPairFAQ[];
}

export const CONVERT_PAIRS: ConvertPair[] = [
  {
    slug: "pes-to-dst",
    from: "pes",
    to: "dst",
    title: "PES to DST Converter Online – Free | Embroidery Online",
    description:
      "Free PES to DST converter online. Upload a Brother PES embroidery file, preview it, resize it if you need to, and download the DST -- no software needed.",
    heading: "PES to DST Converter",
    intro: "Convert a PES file to DST online, free. Upload the PES, preview it, and download the DST.",
    details: [
      "PES is Brother's home-machine format; DST is the Tajima format most commercial and multi-needle machines read. The stitches carry over as they are, so the DST sews the same design the PES did.",
      "DST doesn't record thread colors, only where each color change happens, so the machine will ask you to assign a thread to each block when you load it.",
    ],
    faq: [
      {
        question: "Is this PES to DST converter free?",
        answer: "Yes. Convert as many PES files to DST as you like, with no account and nothing to install.",
      },
      {
        question: "Will my thread colors survive the conversion to DST?",
        answer: "The color changes do, the colors themselves don't -- DST has no place to store them. Pick the threads on your machine.",
      },
      {
        question: "Can I resize the design while converting PES to DST?",
        answer: "Yes. Enter a width or height before converting, or leave both blank to keep the original size.",
      },
    ],
  },
  {
    slug: "dst-to-pes",
    from: "dst",
    to: "pes",
    title: "DST to PES Converter Online – Free | Embroidery Online",
    description:
      "Free DST to PES converter online. Upload a Tajima DST embroidery file, preview it, and download a PES for your Brother machine -- no software needed.",
    heading: "DST to PES Converter",
    intro: "Convert a DST file to PES online, free. Upload the DST, preview it, and download the PES for your Brother machine.",
    details: [
      "This is the usual route for a design bought or downloaded as DST that needs to run on a Brother machine. The stitches carry over unchanged.",
      "The DST had no thread colors to carry over, so the PES uses alternating gray placeholder threads, one per color block, for you to swap for real colors on the machine.",
    ],
    faq: [
      {
        question: "Is this DST to PES converter free?",
        answer: "Yes. Convert as many DST files to PES as you like, with no account and nothing to install.",
      },
      {
        question: "Why is my converted PES file gray?",
        answer: "DST files carry no thread colors, so each color block gets a gray placeholder. Change them to the threads you want on your machine.",
      },
      {
        question: "Will a DST to PES conversion work on my Brother machine?",
        answer: "Yes. PES is the format Brother embroidery machines read, so the converted file loads like any other PES.",
      },
    ],
  },
  {
    slug: "jef-to-pes",
    from: "jef",
    to: "pes",
    title: "JEF to PES Converter Online – Free | Embroidery Online",
    description:
      "Free JEF to PES converter online. Upload a Janome JEF embroidery file, preview it, and download a PES for your Brother machine -- no software needed.",
    heading: "JEF to PES Converter",
    intro: "Convert a Janome JEF file to PES online, free. Upload the JEF, preview it, and download the PES.",
    details: [
      "JEF is Janome's format and PES is Brother's. Converting JEF to PES moves a Janome design onto a Brother machine with the stitches and color order intact.",
    ],
    faq: [
      {
        question: "Is this JEF to PES converter free?",
        answer: "Yes. Convert as many JEF files to PES as you like, with no account and nothing to install.",
      },
      {
        question: "Does converting JEF to PES keep the color order?",
        answer: "Yes. Each color block stays in the same order, so the design sews in the same sequence on a Brother machine.",
      },
      {
        question: "Can I resize the design while converting JEF to PES?",
        answer: "Yes. Enter a width or height before converting, or leave both blank to keep the original size.",
      },
    ],
  },
];

export function convertPairForPath(path: string): ConvertPair | undefined {
  const match = /^\/convert\/([a-z]+-to-[a-z]+)$/.exec(path);
  return match ? CONVERT_PAIRS.find((pair) => pair.slug === match[1]) : undefined;
}

export function convertPairPath(pair: ConvertPair): string {
  return `/convert/${pair.slug}`;
}

// schema.org objects for one pair page, used both by ConvertPage's
// usePageMeta (client) and index.ts's renderPage (server), so the two can't
// disagree. Memoized per pair: usePageMeta re-runs its effect whenever this
// array's identity changes -- see its structuredData comment.
const structuredDataCache = new Map<string, object[]>();

export function convertPairStructuredData(pair: ConvertPair): object[] {
  let data = structuredDataCache.get(pair.slug);
  if (!data) {
    data = [
      {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        name: pair.heading,
        url: `https://embroiderytext.com${convertPairPath(pair)}`,
        description: pair.description,
        applicationCategory: "UtilityApplication",
        operatingSystem: "Any (runs in a web browser)",
        isAccessibleForFree: true,
        offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: pair.faq.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: { "@type": "Answer", text: faq.answer },
        })),
      },
    ];
    structuredDataCache.set(pair.slug, data);
  }
  return data;
}

export function convertPairMarkdown(pair: ConvertPair): string {
  const faq = pair.faq.map((item) => `**${item.question}**\n${item.answer}`).join("\n\n");
  return `# ${pair.heading}

${pair.intro}

${pair.details.join("\n\n")}

Converting a different format? Use the [embroidery file converter](/convert) for every pair between PES, DST, JEF, EXP, and VP3.

## FAQ

${faq}
`;
}
